import React, { useState, useEffect } from 'react';

const Navigation = ({ currentPage, setCurrentPage, mobileNavOpen, setMobileNavOpen, sidebarOpen, user }) => {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // Ouvrir automatiquement le menu Exporter si on est sur une page PDF
  useEffect(() => {
    if (['pdf-emplois', 'pdf-enseignants', 'pdf-bilan'].includes(currentPage)) {
      setExportMenuOpen(true);
    }
  }, [currentPage]);

  const baseMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'sessions', label: 'Séances', icon: '🕐' },
    { id: 'timetables', label: 'Emplois', icon: '🗓️' },
    { id: 'teachers', label: 'Enseignants', icon: '👨' },
    { id: 'classes', label: 'Classes', icon: '🎓' },
    { id: 'studentManagement', label: 'Etudiants', icon: '👥' },
    { id: 'absences', label: 'Absences', icon: '📋' },
    { id: 'courses', label: 'Cours', icon: '📚' },
    { id: 'rooms', label: 'Salles', icon: '🏫' },
    { id: 'departments', label: 'Départements', icon: '🏢' },
    { id: 'university', label: 'Université', icon: '🏛️' },
    { id: 'academic-years', label: 'Années Univ.', icon: '📅' },
    { id: 'academic-year-config', label: 'Horaires', icon: '⚙️' },
    { id: 'grades', label: 'Grades', icon: '🏅' },
    { id: 'tracks', label: 'Parcours', icon: '🛤️' },
    { 
      id: 'export', 
      label: 'Exporter', 
      icon: '📤',
      isSubmenu: true,
      submenu: [
        { id: 'pdf-emplois', label: 'Emplois', icon: '📄' },
        { id: 'pdf-enseignants', label: 'Enseignant', icon: '📄' },
        { id: 'pdf-bilan', label: 'Bilan', icon: '📊' }
      ]
    },
    { id: 'ai-schedule', label: 'IA Emplois', icon: '🤖' },
    { id: 'course-import', label: 'Import Cours', icon: '📥' }
  ];

  const adminMenuItems = [
    { id: 'users', label: 'Utilisateurs', icon: '👥' },
    { id: 'messages', label: 'Messages', icon: '📬' }
  ];

  const menuItems = user?.role === 'admin' 
    ? [...baseMenuItems, ...adminMenuItems]
    : baseMenuItems;

  const handleNavClick = (pageId) => {
    setCurrentPage(pageId);
    setMobileNavOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      
      <nav className={`
        fixed lg:relative inset-y-0 left-0 z-50
        ${sidebarOpen ? 'w-64' : 'w-16'}
        ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-white border-r border-gray-200 transition-all duration-300 ease-in-out
        flex flex-col
      `}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Chronaxis" className="w-8 h-8" />
            {sidebarOpen && <span className="font-semibold text-gray-900">Menu</span>}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4">
          {menuItems.map(item => {
            if (item.isSubmenu) {
              const isExportActive = item.submenu.some(sub => sub.id === currentPage);
              return (
                <div key={item.id}>
                  <button
                    className={`
                      w-full flex items-center px-4 py-2 text-sm font-medium transition-colors
                      ${isExportActive
                        ? 'bg-primary-50 text-primary-700' 
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }
                      ${!sidebarOpen ? 'px-2' : ''}
                    `}
                    onClick={() => sidebarOpen && setExportMenuOpen(!exportMenuOpen)}
                    title={!sidebarOpen ? item.label : ''}
                  >
                    <span className="text-lg">{item.icon}</span>
                    {sidebarOpen && (
                      <>
                        <span className="ml-3 truncate">{item.label}</span>
                        <span className={`ml-2 transition-transform ${exportMenuOpen ? 'rotate-90' : ''}`}>▶</span>
                      </>
                    )}
                  </button>
                  {sidebarOpen && exportMenuOpen && (
                    <div className="bg-gray-50">
                      {item.submenu.map(subItem => (
                        <button
                          key={subItem.id}
                          className={`
                            w-full flex items-center gap-2 pl-12 pr-4 py-2 text-sm font-medium transition-colors
                            ${currentPage === subItem.id 
                              ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600' 
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }
                          `}
                          onClick={() => handleNavClick(subItem.id)}
                        >
                          <span className="text-base">{subItem.icon}</span>
                          <span className="truncate">{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            return (
              <button
                key={item.id}
                className={`
                  w-full flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors
                  ${currentPage === item.id 
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600' 
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                  ${!sidebarOpen ? 'px-2' : ''}
                `}
                onClick={() => handleNavClick(item.id)}
                title={!sidebarOpen ? item.label : ''}
              >
                <span className="text-lg">{item.icon}</span>
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Navigation;